import { useState } from 'react';
import { Badge, Button, Card, ContentLoader, EmptyState, Modal, ModalFooter } from '@/components/common';
import { getErrorMessage } from '@/services';
import { useInsurerPlugins, useSetInsurerSwitch } from '@/hooks/useInsurerPlugins';
import type { InsurerPluginRow, PluginEnv, PluginLine, SwitchState } from '@/services/insurerPlugins';

const ENVS: PluginEnv[] = ['sandbox', 'production'];
const ENV_LABEL: Record<PluginEnv, string> = { sandbox: 'Sandbox', production: 'Production' };
const OTHER_ENV: Record<PluginEnv, PluginEnv> = { sandbox: 'production', production: 'sandbox' };
const COVER_LABEL: Record<string, string> = {
  comprehensive: 'Comprehensive',
  tp_only: 'Third-party only',
  saod: 'Own damage only',
  new: 'New vehicles',
};

interface PendingSwitch {
  plugin: InsurerPluginRow;
  line: PluginLine;
  environment: PluginEnv;
  enabled: boolean;
}

function sourceNote(state: SwitchState): string {
  if (state.source === 'env') return `Forced ${state.enabled ? 'on' : 'off'} by ${state.env_flag} on the server`;
  if (state.source === 'switch') return 'Set by a country admin';
  return 'Plug-in default';
}

function capabilitySummary(line: PluginLine): string[] {
  const caps = line.capabilities;
  const out: string[] = [];
  if (caps.vehicle_categories.length) out.push(`Vehicles: ${caps.vehicle_categories.join(', ')}`);
  if (caps.covers.length) out.push(`Covers: ${caps.covers.map((cover) => COVER_LABEL[cover] ?? cover).join(', ')}`);
  out.push(caps.online_purchase ? 'Sells online' : 'Quotes only (no online purchase)');
  out.push(line.effective_issuance_mode === 'auto' ? 'Issues at once after payment' : 'Issuance awaits insurer verification');
  if (caps.addon_codes.length) out.push(`Add-ons: ${caps.addon_codes.join(', ')}`);
  if (caps.returns_idv_band) out.push('Returns an IDV band');
  return out;
}

function SwitchCell({
  plugin,
  line,
  environment,
  onAsk,
}: {
  plugin: InsurerPluginRow;
  line: PluginLine;
  environment: PluginEnv;
  onAsk: (pending: PendingSwitch) => void;
}) {
  const state = line.switches[environment];
  const forced = state.source === 'env';
  return (
    <div data-testid={`switch-${plugin.code}-${line.line}-${environment}`} className="space-y-1">
      <Badge variant={state.enabled ? 'success' : 'default'} size="sm">
        {state.enabled ? 'ON' : 'OFF'}
      </Badge>
      <p className="text-xs text-gray-500 dark:text-gray-400">{sourceNote(state)}</p>
      <Button
        type="button"
        size="sm"
        variant={state.enabled ? 'outline' : 'primary'}
        disabled={forced}
        onClick={() => onAsk({ plugin, line, environment, enabled: !state.enabled })}
      >
        {state.enabled ? 'Switch off' : 'Switch on'}
      </Button>
    </div>
  );
}

function PluginCard({ plugin, onAsk }: { plugin: InsurerPluginRow; onAsk: (pending: PendingSwitch) => void }) {
  return (
    <Card>
      <div data-testid={`plugin-${plugin.code}`} className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">{plugin.display_name}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {plugin.code} · API {plugin.api_version}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {plugin.relay_required && <Badge variant="info" size="sm">Via Mumbai relay</Badge>}
            {plugin.insurer_id === null && (
              <Badge variant="warning" size="sm">No insurer record with this code</Badge>
            )}
          </div>
        </div>
        <p data-testid={`coverage-${plugin.code}`} className="text-sm text-gray-700 dark:text-gray-300">
          Brokers with credentials — Sandbox: {plugin.credential_coverage.sandbox ?? 0} · Production:{' '}
          {plugin.credential_coverage.production ?? 0}
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            <thead>
              <tr>
                {['Line', 'Capabilities', 'Sandbox', 'Production'].map((heading) => (
                  <th key={heading} className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {plugin.lines.map((line) => (
                <tr key={line.line}>
                  <td className="px-3 py-2 align-top font-medium capitalize text-gray-900 dark:text-white">{line.line}</td>
                  <td className="px-3 py-2 align-top text-xs text-gray-700 dark:text-gray-300">
                    <ul className="space-y-0.5">
                      {capabilitySummary(line).map((text) => (
                        <li key={text}>{text}</li>
                      ))}
                    </ul>
                  </td>
                  {ENVS.map((environment) => (
                    <td key={environment} className="px-3 py-2 align-top">
                      <SwitchCell plugin={plugin} line={line} environment={environment} onAsk={onAsk} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {plugin.masters.length > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Masters: {plugin.masters.map((m) => `${m.command} (${m.sections.join(', ')})`).join('; ')}
          </p>
        )}
      </div>
    </Card>
  );
}

export function InsurerPluginsTab() {
  const { data, isLoading, isError, error, refetch } = useInsurerPlugins();
  const setSwitch = useSetInsurerSwitch();
  const [pending, setPending] = useState<PendingSwitch | null>(null);

  if (isLoading) return <ContentLoader />;
  if (isError) {
    // A failed request is not "no plug-ins".
    return (
      <div role="alert" className="py-8 text-center">
        <p className="text-sm text-red-700 dark:text-red-400">
          Could not load the insurer plug-ins: {getErrorMessage(error)}
        </p>
        <Button className="mt-3" variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const plugins = data?.results ?? [];
  const failures = Object.entries(data?.discovery_errors ?? {});

  return (
    <div className="space-y-6">
      {failures.length > 0 && (
        <div
          role="alert"
          data-testid="discovery-errors"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <p className="font-medium">These plug-ins failed to load and are not in use:</p>
          <ul className="mt-1 list-disc pl-5">
            {failures.map(([module, message]) => (
              <li key={module}>
                <code>{module}</code>: {message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {plugins.length === 0 ? (
        <EmptyState title="No insurer plug-ins" description="No adapter package declares a plugin.py yet." />
      ) : (
        plugins.map((plugin) => <PluginCard key={plugin.code} plugin={plugin} onAsk={setPending} />)
      )}
      <Modal
        isOpen={pending !== null}
        onClose={() => setPending(null)}
        title={
          pending
            ? `Switch ${pending.plugin.display_name} ${pending.line.line} ${pending.enabled ? 'ON' : 'OFF'} in ${ENV_LABEL[pending.environment]}?`
            : ''
        }
      >
        <p className="text-sm text-gray-700 dark:text-gray-300">
          {pending === null
            ? ''
            : pending.enabled
              ? `Brokers with ${ENV_LABEL[pending.environment]} credentials for this insurer start getting live prices from it straight away.`
              : `Every call to this insurer's ${ENV_LABEL[pending.environment]} for this line stops straight away: new quotes, purchases, payment links, confirming or issuing policies already paid for, policy PDFs and status checks. ${ENV_LABEL[OTHER_ENV[pending.environment]]} is not affected. Switch off only when the insurer must not be called.`}
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button
            variant={pending?.enabled ? 'primary' : 'danger'}
            isLoading={setSwitch.isPending}
            onClick={() => {
              if (!pending) return;
              setSwitch.mutate(
                {
                  insurer_code: pending.plugin.code,
                  line: pending.line.line,
                  environment: pending.environment,
                  enabled: pending.enabled,
                },
                { onSettled: () => setPending(null) },
              );
            }}
          >
            {pending?.enabled ? 'Switch on' : 'Switch off'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
